import { useCallback, useEffect, useState } from 'react'
import { Check, Copy, ExternalLink } from 'lucide-react'
import { xaiApi, type XaiLoginView, type XaiSubscriptionStatus } from '../../lib/api'
import { cn } from '../../lib/utils'
import { useStore } from '../../store'
import { Switch } from './TradingRiskSettings'

const when = (t: number) => new Date(t).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
const clock = (ms: number) => {
  const s = Math.max(0, Math.round(ms / 1000))
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}
const errText = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback)
// Matches the server: after "usage ran out", runs start on the API key for 30 minutes.
const EXHAUSTED_BACKOFF_MS = 30 * 60_000

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 px-3 py-2 text-[12.5px]">
      <span className="shrink-0 text-fg-muted">{label}</span>
      <span className="min-w-0 text-right text-fg-subtle">{children}</span>
    </div>
  )
}

/** Connect the admin's own Grok subscription (xAI device-code login); the server keeps the tokens. */
export function XaiSubscriptionSettings() {
  // Only the admin's answers may fall back to the server's API key; everyone else runs on their own plan.
  const isAdmin = useStore((s) => s.user?.role === 'admin')
  const [st, setSt] = useState<XaiSubscriptionStatus | null>(null)
  const [login, setLogin] = useState<XaiLoginView | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirmOff, setConfirmOff] = useState(false)
  const [copied, setCopied] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null)

  const load = useCallback(async () => {
    try {
      const s = await xaiApi.status()
      setSt(s)
      if (s.pending) setLogin(s.pending)
    } catch (e) {
      setMsg({ tone: 'err', text: errText(e, 'Could not load the subscription status') })
    }
  }, [])
  useEffect(() => {
    void load()
  }, [load])

  // While a login is pending the server polls xAI; this only asks the server how it went.
  const pendingId = login?.state === 'pending' ? login.sessionId : null
  useEffect(() => {
    if (!pendingId) return
    const every = Math.min(Math.max(login?.interval ?? 5, 2), 5) * 1000
    const t = setInterval(async () => {
      try {
        const v = await xaiApi.poll(pendingId)
        if (v.state === 'pending') return
        setLogin(null)
        if (v.state === 'connected') setMsg({ tone: 'ok', text: 'Connected — your answers now run on your Grok subscription.' })
        else if (v.state !== 'cancelled') setMsg({ tone: 'err', text: v.message ?? `The login ${v.state === 'denied' ? 'was declined' : 'expired'}.` })
        void load()
      } catch {
        /* keep polling */
      }
    }, every)
    const tick = setInterval(() => setNow(Date.now()), 1000)
    return () => {
      clearInterval(t)
      clearInterval(tick)
    }
  }, [pendingId, login?.interval, load])

  const start = async () => {
    setBusy(true)
    setMsg(null)
    try {
      setLogin(await xaiApi.start())
      setNow(Date.now())
    } catch (e) {
      setMsg({ tone: 'err', text: errText(e, 'xAI did not start the login') })
    } finally {
      setBusy(false)
    }
  }
  const cancel = async () => {
    if (login) await xaiApi.cancel(login.sessionId).catch(() => {})
    setLogin(null)
  }
  const disconnect = async () => {
    setBusy(true)
    try {
      await xaiApi.disconnect()
      setConfirmOff(false)
      setMsg({ tone: 'ok', text: isAdmin ? 'Disconnected — answers use the API key again.' : 'Disconnected — the AI needs your Grok plan to answer.' })
      await load()
    } catch (e) {
      setMsg({ tone: 'err', text: errText(e, 'Could not disconnect') })
    } finally {
      setBusy(false)
    }
  }
  const setFallback = async (v: boolean) => {
    if (!st) return
    setSt({ ...st, fallbackToApi: v })
    try {
      await xaiApi.setFallback(v)
    } catch (e) {
      setSt({ ...st })
      setMsg({ tone: 'err', text: errText(e, 'Could not save') })
    }
  }
  const copy = async () => {
    if (!login) return
    await navigator.clipboard?.writeText(login.userCode).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  const exhaustedRecently = !!st?.exhaustedAt && now - st.exhaustedAt < EXHAUSTED_BACKOFF_MS
  const known = !!st && st.status !== 'not_connected' // a row exists (connected, or needs attention)
  const statusText = !st
    ? ''
    : st.status === 'needs_login'
      ? 'Needs to be reconnected'
      : st.status === 'tier_denied'
        ? 'This plan can’t use the xAI API'
        : exhaustedRecently
          ? `Usage limit reached ${when(st.exhaustedAt!)}${isAdmin ? ' — the API key answers for now' : ' — the AI answers again when your plan’s usage resets'}`
          : 'Active'
  const w = st?.week

  return (
    <section id="settings-xai" className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-[14px] font-semibold text-fg">Grok subscription</h2>
          <p className="mt-0.5 text-[12.5px] text-fg-subtle">
            {isAdmin ? 'Run your answers on your own Grok plan (SuperGrok / X Premium+) instead of the server’s API key.' : 'The AI here runs on your own Grok plan (SuperGrok / X Premium+). Connect it to chat and to let workers call the AI.'}
          </p>
        </div>
        {st?.connected && (
          <span
            className={cn(
              'mt-0.5 shrink-0 rounded-full border px-2 py-0.5 text-[10.5px] font-medium',
              exhaustedRecently ? 'border-[var(--line)] text-fg-subtle' : 'border-[color-mix(in_oklab,var(--color-up)_45%,transparent)] text-up',
            )}
          >
            {exhaustedRecently ? 'Limit reached' : 'Connected'}
          </span>
        )}
      </div>

      {login?.state === 'pending' ? (
        <div className="mt-3 rounded-lg border border-[var(--line-soft)] p-3">
          <div className="text-[12px] text-fg-subtle">Open x.ai, sign in with the account that has the subscription, and approve this code:</div>
          <div className="mt-2 flex items-center gap-2">
            <span className="num select-all rounded-md bg-[var(--color-inset)] px-3 py-1.5 text-[20px] font-semibold tracking-[0.18em] text-fg">
              {login.userCode}
            </span>
            <button onClick={() => void copy()} className="rounded-md p-1.5 text-fg-subtle hover:text-fg" aria-label="Copy the code">
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <a
              href={login.verificationUrlComplete}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-accent)] px-3 py-1.5 text-[12.5px] font-semibold text-[var(--color-base)]"
            >
              Open x.ai to approve <ExternalLink size={12} />
            </a>
            <button onClick={() => void cancel()} className="rounded-lg px-3 py-1.5 text-[12.5px] text-fg-subtle hover:text-fg">
              Cancel
            </button>
          </div>
          <div className="lb-shimmer mt-2.5 text-[11.5px] text-fg-faint">Waiting for your approval · the code expires in {clock(login.expiresAt - now)}</div>
          <div className="mt-1 text-[11px] text-fg-faint">xAI’s page may name the app “Grok Build” — that is the sign-in client this login uses.</div>
        </div>
      ) : known ? (
        <div className="mt-3 divide-y divide-[var(--separator)] rounded-lg border border-[var(--line-soft)]">
          {st!.email && <Row label="Account">{st!.email}</Row>}
          <Row label="Status">
            <span className={cn(st!.connected && !exhaustedRecently ? 'text-fg' : 'text-down')}>{statusText}</span>
            {st!.lastError && (exhaustedRecently || !st!.connected) && <span className="mt-0.5 block text-[11px] text-fg-faint">{st!.lastError}</span>}
          </Row>
          <div className={cn('flex items-center justify-between gap-3 px-3 py-2 text-[12.5px]', !isAdmin && 'hidden')}>
            <span className="min-w-0">
              <span className="text-fg-muted">Continue on the API key when it runs out</span>
              <span className="block text-[11px] text-fg-faint">Answers never stop halfway; the switch is noted under the answer.</span>
            </span>
            <Switch on={st!.fallbackToApi} label="Continue on the API key when the subscription runs out" onChange={(v) => void setFallback(v)} />
          </div>
          {w && (
            <Row label="Last 7 days">
              <span>
                <span className="num">{w.subscription.runs}</span> on the subscription · <span className="num">{w.api.runs}</span> on the API key
                {w.subscription.costUsd > 0 && <span className="block text-[11px] text-fg-faint">≈ ${w.subscription.costUsd.toFixed(2)} of API cost covered by the subscription</span>}
              </span>
            </Row>
          )}
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {!login && (!st?.connected || st.status !== 'active') && (
          <button
            disabled={busy || !st}
            onClick={() => void start()}
            className="rounded-lg bg-[var(--color-accent)] px-3 py-1.5 text-[12.5px] font-semibold text-[var(--color-base)] disabled:opacity-50"
          >
            {known ? 'Reconnect' : 'Connect Grok subscription'}
          </button>
        )}
        {known && !login &&
          (confirmOff ? (
            <>
              <button disabled={busy} onClick={() => void disconnect()} className="rounded-lg border border-[var(--line)] px-3 py-1.5 text-[12.5px] text-down">
                Disconnect now
              </button>
              <button onClick={() => setConfirmOff(false)} className="rounded-lg px-3 py-1.5 text-[12.5px] text-fg-subtle hover:text-fg">
                Keep
              </button>
            </>
          ) : (
            <button onClick={() => setConfirmOff(true)} className="rounded-lg px-3 py-1.5 text-[12.5px] text-fg-subtle hover:text-fg">
              Disconnect
            </button>
          ))}
      </div>
      {msg && <div className={cn('mt-2 text-[12px]', msg.tone === 'ok' ? 'text-up' : 'text-down')}>{msg.text}</div>}
    </section>
  )
}
